import { addMinutes, format, isValid } from 'date-fns';
import { type DateInput, toDate, toDateDayjs } from './parse';

/** Text moment rendered for an invalid date. */
export const INVALID_DATE_TEXT = 'Invalid date';
/** Text dayjs rendered for an invalid date (note the capital D). */
export const INVALID_DATE_TEXT_DAYJS = 'Invalid Date';

/**
 * Formats a leniently parsed value (see `toDate`) in local time with a
 * date-fns format string. Returns `'Invalid date'` instead of throwing, as
 * `moment(value).format(...)` did.
 */
export function formatDate(value: DateInput, formatString: string): string {
  const date = toDate(value);
  return isValid(date) ? format(date, formatString) : INVALID_DATE_TEXT;
}

/**
 * `formatDate` for former dayjs call sites: dayjs string parsing and the
 * `'Invalid Date'` placeholder.
 */
export function formatDateDayjs(
  value: DateInput,
  formatString: string
): string {
  const date = toDateDayjs(value);
  return isValid(date) ? format(date, formatString) : INVALID_DATE_TEXT_DAYJS;
}

/**
 * Formats an instant using its UTC calendar fields (moment's `.utc().format`).
 * Intended for date-only format strings: no zone tokens are supported.
 */
export function formatUtc(date: Date, formatString: string): string {
  if (!isValid(date)) return INVALID_DATE_TEXT;
  const utcFields = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
  utcFields.setFullYear(date.getUTCFullYear());
  return format(utcFields, formatString);
}

/** moment's default UTC `format()`: `2024-01-31T17:05:09Z`. */
export function formatIsoUtc(date: Date): string {
  if (!isValid(date)) return INVALID_DATE_TEXT;
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** moment's / dayjs' default local `format()`: `2024-01-31T12:05:09-05:00`. */
export function formatIsoLocal(date: Date): string {
  if (!isValid(date)) return INVALID_DATE_TEXT;
  return format(date, "yyyy-MM-dd'T'HH:mm:ssxxx");
}

/**
 * The current wall-clock time shifted forward by the local UTC offset and
 * rendered as `yyyy-MM-dd HH:mm:ss` (the former
 * `moment().add(moment().utcOffset(), 'minutes')` zip timestamp).
 */
export function formatNowShiftedByUtcOffset(now: Date = new Date()): string {
  const utcOffsetMinutes = -Math.round(now.getTimezoneOffset());
  return format(addMinutes(now, utcOffsetMinutes), 'yyyy-MM-dd HH:mm:ss');
}
